import { Injectable, inject } from "@angular/core";
import { Checklist } from "@domain/project/checklist";
import { ChecklistItemSecondaryDisplays, ChecklistMonitoring } from "@domain/project/checklist-monitoring";
import { FgdsProject } from "@domain/project/fgds-project";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ChecklistMonitoringComponent } from "@pages/project/project-details/checklist/checklist-monitoring.component";
import { ChecklistRow } from "@pdf/export-services/checklist-page/checklist-row";
import { ChecklistTable } from "@pdf/export-services/checklist-page/checklist-table";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { HalfPageContainerService } from "@pdf/helpers/container/half-page/half-page-container.service";
import { TableColumn } from "@pdf/helpers/default-table/table-column";
import { DraegerFonts } from "@pdf/helpers/draeger-fonts";
import {
  ContainerProperties,
  FontNames,
  Headlines,
  ImagePaths,
  PdfProperties,
  ProductListProperties,
  TableProperties,
} from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { Observable } from "rxjs";

interface ChecklistTableRowData {
  title: string;
  text: string;
  checked?: boolean;
}

export class ChecklistHeadlines {
  public readonly GENERAL = $localize`:@@pdfExport.checklist.general:Allgemein`;
  public readonly INSTALLATION_INSTRUCTIONS = $localize`:@@pdfExport.checklist.installationInstructions:Installationsanweisungen`;
  public readonly SERVICE_INSTRUCTIONS = $localize`:@@pdfExport.checklist.serviceInstructions:Wartungsanweisungen`;
  public readonly RISK_ASSESSMENT = $localize`:@@pdfExport.checklist.riskAssessment:Risikobewertung`;
}

@Injectable({
  providedIn: "root",
})
export class ChecklistPagePdfService {
  private containerService = inject(HalfPageContainerService);

  private headlines = new Headlines();
  readonly checklistHeadlines = new ChecklistHeadlines();

  generateFgds(
    pdf: jsPDF,
    project: FgdsProject,
    tableOfContents: TableOfContents,
    container: HalfPageContainer,
    isRiskAssessmentIncluded: boolean,
  ): Observable<jsPDF> {
    return new Observable((observer) => {
      this.addFonts(pdf);
      pdf.setFont(FontNames.DRAEGER_PANGEA);
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
      pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      this.addChecklistTables(pdf, container, tableOfContents, project.checklist, isRiskAssessmentIncluded);
      observer.next(pdf);
      observer.complete();
    });
  }

  private addChecklistTables(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    checklist: Checklist,
    isRiskAssessmentIncluded: boolean,
  ) {
    const checklistTableRows: ChecklistTableRowData[] = this.getChecklistTableRowData(checklist);
    const checklistTableRowsInstallationInstructions: ChecklistTableRowData[] =
      this.getChecklistTableRowDataInstallationInstructions(checklist);
    const checklistTableRowsServiceInstructions: ChecklistTableRowData[] =
      this.getChecklistTableRowDataServiceInstructions(checklist);

    let checklistTables: ChecklistTable[];
    if (isRiskAssessmentIncluded) {
      const checklistTableRowsRiskAssessment: ChecklistTableRowData[] = this.getChecklistTableRowDataRiskAssessment(checklist);
      checklistTables = [
        new ChecklistTable(this.checklistHeadlines.GENERAL, pdf, checklistTableRows),
        new ChecklistTable(this.checklistHeadlines.INSTALLATION_INSTRUCTIONS, pdf, checklistTableRowsInstallationInstructions),
        new ChecklistTable(this.checklistHeadlines.SERVICE_INSTRUCTIONS, pdf, checklistTableRowsServiceInstructions),
        new ChecklistTable(this.checklistHeadlines.RISK_ASSESSMENT, pdf, checklistTableRowsRiskAssessment),
      ];
    } else {
      checklistTables = [
        new ChecklistTable(this.checklistHeadlines.GENERAL, pdf, checklistTableRows),
        new ChecklistTable(this.checklistHeadlines.INSTALLATION_INSTRUCTIONS, pdf, checklistTableRowsInstallationInstructions),
        new ChecklistTable(this.checklistHeadlines.SERVICE_INSTRUCTIONS, pdf, checklistTableRowsServiceInstructions),
      ];
    }
    this.drawChecklist(pdf, container, tableOfContents, checklistTables);
  }

  private drawChecklist(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    checklistTables: ChecklistTable[],
  ) {
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    const pageHeadline = this.headlines.CHECKLIST;
    this.containerService.addOnNewPage(pdf, container, pageHeadline, tableOfContents);
    container.yPosition += TableProperties.HEADLINE_OFFSET_Y;

    checklistTables.forEach((table) => {
      this.drawChecklistTable(table, pdf, container, pageHeadline, tableOfContents);
    });
  }

  private drawChecklistTable(
    checklistTable: ChecklistTable,
    pdf: jsPDF,
    container: HalfPageContainer,
    pageHeadline: string,
    tableOfContents: TableOfContents,
  ) {
    pdf.setFont(FontNames.DRAEGER_PANGEA);
    pdf.setFontSize(TableProperties.HEADER_FONT_SIZE);
    pdf.text(checklistTable.headline, container.xPosition + TableProperties.PADDING_X, container.yPosition, { baseline: "top" });
    container.yPosition += TableProperties.PADDING_48;
    pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT);

    checklistTable.rows.forEach((row) => {
      const isEnoughSpace = this.checkIfIsEnoughSpace(row.textHeight + TableProperties.ROW_PADDING_Y, container);
      if (!isEnoughSpace) {
        this.containerService.add(pdf, container, pageHeadline, tableOfContents);
        container.yPosition += TableProperties.PADDING_48;
        pdf.setFont(FontNames.DRAEGER_PANGEA);
        pdf.setFontSize(TableProperties.HEADER_FONT_SIZE);
        pdf.text(checklistTable.headline, container.xPosition + TableProperties.PADDING_X, container.yPosition, {
          baseline: "top",
        });
        container.yPosition += TableProperties.PADDING_48;
        pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT);
      }
      this.addChecklistRow(pdf, container, checklistTable.columns, row);
    });
    container.yPosition += TableProperties.GAP_BETWEEN_TABLES;
  }

  private addChecklistRow(pdf: jsPDF, container: HalfPageContainer, columns: TableColumn[], row: ChecklistRow) {
    container.yPosition += TableProperties.ROW_PADDING_TOP;
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    row.title.forEach((line, index) => {
      pdf.text(
        line,
        container.xPosition + columns[0].xPosition,
        container.yPosition + index * pdf.getTextDimensions(line).h * PdfProperties.POINT_TO_PIXEL_RATIO,
        { baseline: "top" },
      );
    });
    if (row.checked !== undefined) {
      const icon = row.checked ? ImagePaths.SWITCH_CHECKED : ImagePaths.SWITCH_UNCHECKED;
      pdf.addImage(icon, "PNG", container.xPosition + columns[1].xPosition, container.yPosition - 3, 52, 30);
    }
    row.text.forEach((line, index) => {
      pdf.text(
        line,
        container.xPosition + columns[2].xPosition,
        container.yPosition + index * pdf.getTextDimensions(line).h * PdfProperties.POINT_TO_PIXEL_RATIO,
        { baseline: "top" },
      );
    });
    container.yPosition += row.textHeight;
    container.yPosition += TableProperties.ROW_PADDING_BOTTOM;
  }

  private checkIfIsEnoughSpace(height: number, container: HalfPageContainer) {
    return (
      ContainerProperties.Y + ContainerProperties.HEIGHT - ProductListProperties.FOOTER_HEIGHT - container.yPosition > height
    );
  }

  private getChecklistTableRowData(checklist: Checklist): ChecklistTableRowData[] {
    return [
      {
        title: $localize`:@@checklist.operator.label:Wer ist Betreiber der Anlage?`,
        text: checklist.operator || "",
      },
      { title: $localize`:@@checklist.client.label:Wer ist Auftraggeber?`, text: checklist.client || "" },
      {
        title: $localize`:@@checklist.switch1.label:Sind alle Sensoren mit Gasart und Messbereich berücksichtigt?`,
        checked: checklist.sensorGasAndMeasurementRangeConsidered.checked,
        text: checklist.sensorGasAndMeasurementRangeConsidered.notes || "",
      },
      {
        title: $localize`:@@checklist.switch2.label:Gibt es Störeinflüsse (Sensorgifte, etc.)?`,
        checked: checklist.peturbationsExist.checked,
        text: checklist.peturbationsExist.notes || "",
      },
      {
        title: $localize`:@@checklist.switch3.label:Sind alle benötigten Relaisausgänge berücksichtigt?`,
        checked: checklist.relayOutputsConsidered.checked,
        text: checklist.relayOutputsConsidered.notes || "",
      },
      {
        title: $localize`:@@checklist.switch4.label:Ist die Betriebsspannung der Alarmmittel definiert?`,
        checked: checklist.operatingVoltageOfMeansOfAlarmDefined.checked,
        text: checklist.operatingVoltageOfMeansOfAlarmDefined.notes || "",
      },
      {
        title: $localize`:@@checklist.switch5.label:Sind alle Sensoren / Gaswarnzentrale / Alarmmittel positioniert?`,
        checked: checklist.sensorsGasAlarmDeviceAndMeansOfAlarmSet.checked,
        text: checklist.sensorsGasAlarmDeviceAndMeansOfAlarmSet.notes || "",
      },
      {
        title: $localize`:@@checklist.switch6.label:Anforderungen Installation / Montage`,
        checked: checklist.installationRequirementsDefined.checked,
        text: checklist.installationRequirementsDefined.notes || "",
      },
      {
        title: $localize`:@@checklist.switch7.label:Gibt es Werksnormen / besondere Auflagen?`,
        checked: checklist.specialStandardsExist.checked,
        text: checklist.specialStandardsExist.notes || "",
      },
      {
        title: $localize`:@@checklist.switch8.label:Werden Steiger / Gerüste benötigt / bereitgestellt?`,
        checked: checklist.scaffoldingRequired.checked,
        text: checklist.scaffoldingRequired.notes || "",
      },
      {
        title: $localize`:@@checklist.switch9.label:Gibt es Ex-Zonen?`,
        checked: checklist.exZonesExist.checked,
        text: checklist.exZonesExist.notes || "",
      },
      {
        title: $localize`:@@checklist.switch10.label:Freimessung notwendig? Wenn ja, durch wen?`,
        checked: checklist.clearanceMeasurementRequired.checked,
        text: checklist.clearanceMeasurementRequired.notes || "",
      },
      {
        title: $localize`:@@checklist.switch11.label:Ist ein Erdungskonzept vorhanden?`,
        checked: checklist.electricalGroundingConceptExists.checked,
        text: checklist.electricalGroundingConceptExists.notes || "",
      },
      {
        title: $localize`:@@checklist.switch12.label:Ist eine Be-/Entlüftung vorhanden?`,
        checked: checklist.ventilationExists.checked,
        text: checklist.ventilationExists.notes || "",
      },
      {
        title: $localize`:@@checklist.personToInstruct.label:Wer ist bei der IBN zugegen, bzw. wer soll eingewiesen werden?`,
        text: checklist.personToInstruct || "",
      },
    ];
  }

  private getChecklistTableRowDataInstallationInstructions(checklist: Checklist) {
    return [
      {
        title: $localize`:@@checklist.installationInstructions.cableType.label:Kabeltyp?`,
        checked: checklist.cableType.checked,
        text: checklist.cableType.notes || "",
      },
      {
        title: $localize`:@@checklist.installationInstructions.sil.label:SIL?`,
        checked: checklist.sil.checked,
        text: checklist.sil.notes || "",
      },

      {
        title: $localize`:@@checklist.installationInstructions.workingHeight.label:Arbeitshöhe?`,
        checked: checklist.workingHeight.checked,
        text: checklist.workingHeight.notes || "",
      },
      {
        title: $localize`:@@checklist.installationInstructions.deliverySpecifics.label:Lieferhinweise?`,
        checked: checklist.deliverySpecifics.checked,
        text: checklist.deliverySpecifics.notes || "",
      },
      {
        title: $localize`:@@checklist.installationInstructions.otherSpecifics.label:Weitere Angaben?`,
        checked: checklist.otherSpecifics.checked,
        text: checklist.otherSpecifics.notes || "",
      },
    ];
  }

  private getChecklistTableRowDataServiceInstructions(checklist: Checklist) {
    return [
      {
        title: $localize`:@@checklist.serviceInstructions.calibrationGas.label:Kalibriergas?`,
        checked: checklist.calibrationGas.checked,
        text: checklist.calibrationGas.notes || "",
      },
      {
        title: $localize`:@@checklist.serviceInstructions.calibrationInstructions.label:Kalibrierungsanweisungen?`,
        checked: checklist.calibrationInstructions.checked,
        text: checklist.calibrationInstructions.notes || "",
      },
    ];
  }

  private getChecklistTableRowDataRiskAssessment(checklist: Checklist) {
    return [
      {
        title: $localize`:@@checklist.riskAssessment.deadlineRisks.label:Deadline-Risiken?`,
        checked: checklist.deadlineRisks.checked,
        text: checklist.deadlineRisks.notes || "",
      },
      {
        title: $localize`:@@checklist.riskAssessment.technicalRisks.label:Technische Risiken?`,
        checked: checklist.technicalRisks.checked,
        text: checklist.technicalRisks.notes || "",
      },
      {
        title: $localize`:@@checklist.riskAssessment.commercialRisks.label:Geschäftliche Risiken?`,
        checked: checklist.commercialRisks.checked,
        text: checklist.commercialRisks.notes || "",
      },
      {
        title: $localize`:@@checklist.riskAssessment.otherRisks.label:Weitere Risiken?`,
        checked: checklist.otherRisks.checked,
        text: checklist.otherRisks.notes || "",
      },
    ];
  }

  generateMonitoring(
    pdf: jsPDF,
    project: MonitoringProject,
    tableOfContents: TableOfContents,
    container: HalfPageContainer,
    isRiskAssessmentIncluded: boolean,
  ): Observable<jsPDF> {
    return new Observable((observer) => {
      this.addFonts(pdf);
      pdf.setFont(FontNames.DRAEGER_PANGEA);
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
      pdf.setFont(FontNames.DRAEGER_PANGEA, "normal");
      pdf.setTextColor(PdfProperties.DRAEGERBLUE);
      this.addMonitoringChecklistTables(pdf, container, tableOfContents, project.checklistMonitoring, isRiskAssessmentIncluded);
      observer.next(pdf);
      observer.complete();
    });
  }

  private addMonitoringChecklistTables(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    checklist: ChecklistMonitoring,
    isRiskAssessmentIncluded: boolean,
  ) {
    const checklistTableRows: ChecklistTableRowData[] = this.getMonitoringChecklistTableRowData(checklist);
    const checklistTableRowsInstallationInstructions: ChecklistTableRowData[] =
      this.getMonitoringChecklistTableRowDataInstallationInstructions(checklist);

    let checklistTables: ChecklistTable[];
    if (isRiskAssessmentIncluded) {
      const checklistTableRowsRiskAssessment: ChecklistTableRowData[] =
        this.getMonitoringChecklistTableRowDataRiskAssessment(checklist);
      checklistTables = [
        new ChecklistTable(this.checklistHeadlines.GENERAL, pdf, checklistTableRows),
        new ChecklistTable(this.checklistHeadlines.INSTALLATION_INSTRUCTIONS, pdf, checklistTableRowsInstallationInstructions),
        new ChecklistTable(this.checklistHeadlines.RISK_ASSESSMENT, pdf, checklistTableRowsRiskAssessment),
      ];
    } else {
      checklistTables = [
        new ChecklistTable(this.checklistHeadlines.GENERAL, pdf, checklistTableRows),
        new ChecklistTable(this.checklistHeadlines.INSTALLATION_INSTRUCTIONS, pdf, checklistTableRowsInstallationInstructions),
      ];
    }
    this.drawChecklist(pdf, container, tableOfContents, checklistTables);
  }

  private getMonitoringChecklistTableRowData(checklist: ChecklistMonitoring): ChecklistTableRowData[] {
    return [
      {
        title: $localize`:@@checklistMonitoring.existingInstallBase.label:Existing Install Base?`,
        checked: checklist.existingInstallBase.checked,
        text: checklist.existingInstallBase.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.networked.label:Networked?`,
        checked: checklist.networked.checked,
        text: this.mapChecklistSingleChoiceOption("networked", checklist.networked.choice),
      },
      {
        title: $localize`:@@checklistMonitoring.networkCableSupply.label:Are we supplying network cables?`,
        checked: checklist.networkCableSupply.checked,
        text: checklist.networkCableSupply.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.wirelessNetworkRequired.label:Wireless Network Required?`,
        checked: checklist.wirelessNetworkRequired.checked,
        text: checklist.wirelessNetworkRequired.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.deviceInterfacingRequired.label:Is device interfacing required`,
        checked: checklist.deviceInterfacingRequired.checked,
        text: checklist.deviceInterfacingRequired.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.centralStationIncluded.label:Includes Central Station?`,
        checked: checklist.centralStationIncluded.checked,
        text: this.mapChecklistMultipleChoiceOption("centralStationIncluded", checklist.centralStationIncluded.choices),
      },
      {
        title: $localize`:@@checklistMonitoring.secondaryDisplaysIncluded.label:Secondary Displays included?`,
        checked: checklist.secondaryDisplaysIncluded.checked,
        text: this.mapChecklistSecondaryDisplaysOption(checklist.secondaryDisplaysIncluded),
      },
      {
        title: $localize`:@@checklistMonitoring.ownPrinter.label:Is hospital using their own printer?`,
        checked: checklist.ownPrinter.checked,
        text: checklist.ownPrinter.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.itAppsIncluded.label:Includes IT apps?`,
        checked: checklist.itAppsIncluded.checked,
        text: this.mapChecklistMultipleChoiceOption("itAppsIncluded", checklist.itAppsIncluded.choices),
      },
      {
        title: $localize`:@@checklistMonitoring.digitalSolutionsIncluded.label:Includes Digital solutions`,
        checked: checklist.digitalSolutionsIncluded.checked,
        text: checklist.digitalSolutionsIncluded.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.serverLocation.label:Server Location?`,
        checked: checklist.serverLocation.checked,
        text: this.mapChecklistSingleChoiceOption("serverLocation", checklist.serverLocation.choice),
      },
      {
        title: $localize`:@@checklistMonitoring.additionalInformation.label:Additional Information`,
        checked: checklist.additionalInformation.checked,
        text: checklist.additionalInformation.notes || "",
      },
    ];
  }

  private getMonitoringChecklistTableRowDataInstallationInstructions(checklist: ChecklistMonitoring): ChecklistTableRowData[] {
    return [
      {
        title: $localize`:@@checklistMonitoring.mountInstaller.label:Who will be installing wall or ceiling mounts?`,
        checked: checklist.mountInstaller.checked,
        text: this.mapChecklistSingleChoiceOption("mountInstaller", checklist.mountInstaller.choice),
      },
      {
        title: $localize`:@@checklistMonitoring.earlyChannelSupply.label:Do wall channels need to be supplied early?`,
        checked: checklist.earlyChannelSupply.checked,
        text: checklist.earlyChannelSupply.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.earlyItInvolvementRequired.label:Is early IT involvement required?`,
        checked: checklist.earlyItInvolvementRequired.checked,
        text: this.mapChecklistSingleChoiceOption("earlyItInvolvementRequired", checklist.earlyItInvolvementRequired.choice),
      },
      {
        title: $localize`:@@checklistMonitoring.ipAddressRangeProvided.label:Has an IP address range been provided?`,
        checked: checklist.ipAddressRangeProvided.checked,
        text: checklist.ipAddressRangeProvided.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.departmentNamesProvided.label:Do we have department names?`,
        checked: checklist.departmentNamesProvided.checked,
        text: checklist.departmentNamesProvided.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.bedNamesProvided.label:Do we have bed names?`,
        checked: checklist.bedNamesProvided.checked,
        text: checklist.bedNamesProvided.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.hospitalShortNameProvided.label:Do we have a hospital short name?`,
        checked: checklist.hospitalShortNameProvided.checked,
        text: checklist.hospitalShortNameProvided.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.hospitalPrinterIpsProvided.label:Do we have hospital printer IP's?`,
        checked: checklist.hospitalPrinterIpsProvided.checked,
        text: checklist.hospitalPrinterIpsProvided.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.commissioningRequirementsProvided.label:What commissioning is required?`,
        checked: checklist.commissioningRequirementsProvided.checked,
        text: this.mapChecklistSingleChoiceOption(
          "commissioningRequirementsProvided",
          checklist.commissioningRequirementsProvided.choice,
        ),
      },
      {
        title: $localize`:@@checklistMonitoring.deliveryDetails.label:Delivery details`,
        checked: checklist.deliveryDetails.checked,
        text: checklist.deliveryDetails.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.estimatedGoLiveDate.label:Estimated Go-Live date`,
        checked: checklist.estimatedGoLiveDate.checked,
        text: checklist.estimatedGoLiveDate.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.additionalInstallationInformation.label:Additional information`,
        checked: checklist.additionalInstallationInformation.checked,
        text: checklist.additionalInstallationInformation.notes || "",
      },
    ];
  }

  private getMonitoringChecklistTableRowDataRiskAssessment(checklist: ChecklistMonitoring): ChecklistTableRowData[] {
    return [
      {
        title: $localize`:@@checklistMonitoring.deadlineRisks.label:Deadline Risks?`,
        checked: checklist.deadlineRisks.checked,
        text: checklist.deadlineRisks.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.technicalRisks.label:Technical Risks?`,
        checked: checklist.technicalRisks.checked,
        text: checklist.technicalRisks.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.otherRisks.label:Other Risks?`,
        checked: checklist.otherRisks.checked,
        text: checklist.otherRisks.notes || "",
      },
      {
        title: $localize`:@@checklistMonitoring.discussionNotes.label:Discussion Notes`,
        checked: checklist.discussionNotes.checked,
        text: checklist.discussionNotes.notes || "",
      },
    ];
  }

  private mapChecklistSingleChoiceOption(key: string, choice: string | undefined): string {
    const options = ChecklistMonitoringComponent.getChecklistOptions()[key];

    if (options) {
      const selectedChoice = options.find((item) => item.value === choice);

      return selectedChoice?.label ?? "";
    }

    return "";
  }

  private mapChecklistMultipleChoiceOption(key: string, choices: string[] | undefined): string {
    if (!choices) {
      return "";
    }

    const options = ChecklistMonitoringComponent.getChecklistOptions()[key];

    if (options) {
      return options
        .filter((item) => choices.includes(item.value))
        .map((item) => options.find((option) => option.value === item.value))
        .map((item) => item?.label)
        .filter((item) => !!item)
        .join(", ");
    }

    return "";
  }

  private mapChecklistSecondaryDisplaysOption(checklistItem: ChecklistItemSecondaryDisplays): string {
    if (!checklistItem.choices) {
      return "";
    }

    const options = ChecklistMonitoringComponent.getChecklistOptions()["secondaryDisplaysIncluded"];

    let result = "";

    if (options) {
      result += options
        .filter((item) => checklistItem.choices!.includes(item.value))
        .map((item) => options.find((option) => option.value === item.value))
        .map((item) => item?.label)
        .filter((item) => !!item)
        .join(", ");
    }

    if (checklistItem.networkChoice) {
      const options = ChecklistMonitoringComponent.getChecklistOptions()["secondaryDisplaysNetwork"];

      const choice = options.find((option) => option.value === checklistItem.networkChoice);

      if (choice) {
        result += `\n${choice.label}`;
      }
    }

    if (checklistItem.connectionToChoice) {
      const options = ChecklistMonitoringComponent.getChecklistOptions()["secondaryDisplaysConnectionTo"];

      const choice = options.find((option) => option.value === checklistItem.connectionToChoice);

      if (choice) {
        result += `\n${choice.label}`;
      }
    }

    if (checklistItem.notes) {
      result += `\n${checklistItem.notes}`;
    }

    return result;
  }

  private addFonts(doc: jsPDF) {
    new DraegerFonts().fonts.forEach((font) => {
      doc.addFileToVFS(font.ttfName, font.base64);
      doc.addFont(font.ttfName, font.fontName, font.fontWeight);
    });
  }
}
